// components/AppLauncher/AppGallery/hooks/useAppFiltering.js
import { useState, useMemo, useEffect } from 'react';

export const useAppFiltering = (apps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showCategories, setShowCategories] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [enabledCategories, setEnabledCategories] = useState(new Set());
  // Extract and sort categories
  const categories = useMemo(() => {
    if (!apps?.length) return [];
    
    // Get unique categories, including 'Uncategorized' for null/empty
    const cats = apps.reduce((acc, app) => {
      const category = app.category?.trim() || 'Uncategorized';
      acc.add(category);
      return acc;
    }, new Set());

    const sortedCats = Array.from(cats).sort();
    return sortedCats;
  }, [apps]);

  // Re-initialize categories as enabled and expanded whenever the category list changes
  useEffect(() => {
    if (categories.length) {
      setEnabledCategories(new Set(categories));
      setExpandedCategories(new Set(categories));
    }
  }, [categories]);

  // Process apps
  const processedApps = useMemo(() => {
    if (!apps?.length) return [];

    // console.log('Processing apps:', apps.map(app => ({
    //   id: app.id,
    //   name: app.name,
    //   type: app.plugin_type,
    //   category: app.category,
    //   thumbnail: app.thumbnail_url
    // })));

    // Filter by search and category
    const filtered = apps.filter(app => {
      const matchesSearch = !searchTerm || 
        app.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const category = app.category?.trim() || 'Uncategorized';
      const matchesCategory = enabledCategories.has(category);

      return matchesSearch && matchesCategory;
    });

    // Sort by display order and name
    const sorted = [...filtered].sort((a, b) => {
      const orderDiff = (b.display_order || 0) - (a.display_order || 0);
      return orderDiff || (a.name || '').localeCompare(b.name || '');
    });

    if (!showCategories) {
      // console.log('Flat view - sorted apps:', sorted.map(a => a.name));
      return sorted;
    }

    // Group by category
    const grouped = sorted.reduce((acc, app) => {
      const category = app.category?.trim() || 'Uncategorized';
      if (!acc[category]) acc[category] = [];
      acc[category].push(app);
      return acc;
    }, {});

    const categorized = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
    // console.log('Categorized apps:', Object.fromEntries(categorized));
    
    return categorized;
  }, [apps, searchTerm, enabledCategories, showCategories]);

  return {
    searchTerm,
    setSearchTerm,
    showCategories,
    setShowCategories,
    expandedCategories,
    setExpandedCategories,
    enabledCategories,
    setEnabledCategories,
    categories,
    processedApps,
  };
};